import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Nav, Spinner } from "react-bootstrap";
import { sendEntityShare } from "../../actions/chatActions";
import { fetchEntityShareCandidates } from "../../features/chat/api/entityShareCandidates";
import {
  ENTITY_SHARE_TRANSPORT_ERROR,
  createEntityShareRequestId,
  registerEntityShareRequest,
} from "../../features/chat/entityShareRequests";

const EMPTY_BOTH_PARTICIPANTS =
  "Немає карток, доступних обом учасникам цього чату.";
const LOAD_ERROR = "Не вдалося завантажити список.";
const LOAD_MORE_ERROR = "Не вдалося завантажити ще картки.";
const SEND_GENERIC_ERROR = "Неможливо надіслати картку.";
const PAGE_SIZE = 20;

function unwrapCandidates(data) {
  if (Array.isArray(data)) return data;
  return data?.results || [];
}

function mergeUniqueRows(existing, incoming) {
  const seen = new Set(existing.map((row) => `${row.type || ""}:${row.id}`));
  const merged = existing.slice();
  incoming.forEach((row) => {
    const key = `${row.type || ""}:${row.id}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  });
  return merged;
}

function isAbortError(err, controller) {
  return (
    err?.name === "CanceledError"
    || err?.name === "AbortError"
    || controller?.signal?.aborted
  );
}

export default function EntitySharePicker({ chatId, show, onHide }) {
  const [activeTab, setActiveTab] = useState("clients");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [loadMoreError, setLoadMoreError] = useState("");
  const [sendError, setSendError] = useState("");
  const [sending, setSending] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const sendingRef = useRef(false);
  const mountedRef = useRef(true);
  const unregisterRef = useRef(null);
  const loadAbortRef = useRef(null);
  const loadMoreAbortRef = useRef(null);
  const loadGenRef = useRef(0);

  const invalidatePendingLoads = () => {
    loadGenRef.current += 1;
    if (loadAbortRef.current) {
      loadAbortRef.current.abort();
      loadAbortRef.current = null;
    }
    if (loadMoreAbortRef.current) {
      loadMoreAbortRef.current.abort();
      loadMoreAbortRef.current = null;
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      invalidatePendingLoads();
      if (unregisterRef.current) {
        unregisterRef.current();
        unregisterRef.current = null;
      }
      sendingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/unmount only
  }, []);

  useEffect(() => {
    if (!show) {
      invalidatePendingLoads();
      setSearch("");
      setRows([]);
      setTotalCount(0);
      setError("");
      setLoadMoreError("");
      setSendError("");
      setActiveTab("clients");
      setSending(false);
      setLoading(false);
      setLoadingMore(false);
      sendingRef.current = false;
      if (unregisterRef.current) {
        unregisterRef.current();
        unregisterRef.current = null;
      }
      return;
    }

    if (!chatId) {
      invalidatePendingLoads();
      setRows([]);
      setTotalCount(0);
      setError(LOAD_ERROR);
      setLoadMoreError("");
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    const generation = ++loadGenRef.current;
    if (loadAbortRef.current) {
      loadAbortRef.current.abort();
    }
    if (loadMoreAbortRef.current) {
      loadMoreAbortRef.current.abort();
      loadMoreAbortRef.current = null;
    }
    const controller = typeof AbortController !== "undefined"
      ? new AbortController()
      : null;
    loadAbortRef.current = controller;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadingMore(false);
      setError("");
      setLoadMoreError("");
      setRows([]);
      setTotalCount(0);
      try {
        const entityType = activeTab === "clients" ? "client" : "property";
        const params = {
          type: entityType,
          limit: PAGE_SIZE,
          offset: 0,
        };
        if (search.trim()) {
          params.search = search.trim();
        }
        const data = await fetchEntityShareCandidates(chatId, params, {
          signal: controller?.signal,
        });
        if (cancelled || generation !== loadGenRef.current || !mountedRef.current) {
          return;
        }
        setRows(unwrapCandidates(data));
        setTotalCount(typeof data?.count === "number" ? data.count : unwrapCandidates(data).length);
      } catch (err) {
        if (isAbortError(err, controller)) {
          return;
        }
        if (!cancelled && generation === loadGenRef.current && mountedRef.current) {
          setError(LOAD_ERROR);
          setRows([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled && generation === loadGenRef.current && mountedRef.current) {
          setLoading(false);
        }
      }
    };
    const timer = setTimeout(load, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller?.abort();
      if (loadMoreAbortRef.current) {
        loadMoreAbortRef.current.abort();
        loadMoreAbortRef.current = null;
      }
      // Ensure a superseded load-more cannot leave the button stuck.
      setLoadingMore(false);
    };
  }, [show, activeTab, search, chatId, reloadKey]);

  const items = useMemo(() => {
    return rows.map((row) => ({
      id: row.id,
      label: row.label || (activeTab === "clients" ? "Клієнт" : "Об'єкт"),
      secondary: typeof row.secondary === "string" ? row.secondary : "",
      type: row.type || (activeTab === "clients" ? "client" : "property"),
      thumbnail: typeof row.thumbnail === "string" && row.thumbnail
        ? row.thumbnail
        : null,
    }));
  }, [rows, activeTab]);

  const hasMore = rows.length < totalCount;

  const handleLoadMore = async () => {
    if (loadingMore || loading || sending || !chatId || !hasMore) return;
    const generation = loadGenRef.current;
    if (loadMoreAbortRef.current) {
      loadMoreAbortRef.current.abort();
    }
    const controller = typeof AbortController !== "undefined"
      ? new AbortController()
      : null;
    loadMoreAbortRef.current = controller;
    setLoadingMore(true);
    setLoadMoreError("");
    try {
      const entityType = activeTab === "clients" ? "client" : "property";
      const params = {
        type: entityType,
        limit: PAGE_SIZE,
        offset: rows.length,
      };
      if (search.trim()) {
        params.search = search.trim();
      }
      const data = await fetchEntityShareCandidates(chatId, params, {
        signal: controller?.signal,
      });
      if (generation !== loadGenRef.current || !mountedRef.current) {
        return;
      }
      const next = unwrapCandidates(data);
      setRows((prev) => mergeUniqueRows(prev, next));
      if (typeof data?.count === "number") {
        setTotalCount(data.count);
      }
    } catch (err) {
      if (isAbortError(err, controller)) {
        return;
      }
      if (generation === loadGenRef.current && mountedRef.current) {
        setLoadMoreError(LOAD_MORE_ERROR);
      }
    } finally {
      if (loadMoreAbortRef.current === controller) {
        loadMoreAbortRef.current = null;
      }
      if (generation === loadGenRef.current && mountedRef.current) {
        setLoadingMore(false);
      }
    }
  };

  const resetSendState = () => {
    sendingRef.current = false;
    setSending(false);
  };

  const handleSelect = (item) => {
    if (sendingRef.current || sending || !chatId) return;
    sendingRef.current = true;
    setSending(true);
    setSendError("");

    const requestId = createEntityShareRequestId();
    if (unregisterRef.current) {
      unregisterRef.current();
    }
    unregisterRef.current = registerEntityShareRequest(requestId, {
      onSuccess: () => {
        unregisterRef.current = null;
        if (!mountedRef.current) return;
        resetSendState();
        onHide();
      },
      onError: (result) => {
        unregisterRef.current = null;
        if (!mountedRef.current) return;
        resetSendState();
        setSendError(result?.detail || SEND_GENERIC_ERROR);
      },
    });

    const accepted = sendEntityShare(chatId, item.type, item.id, requestId);
    if (accepted === false) {
      if (unregisterRef.current) {
        unregisterRef.current();
        unregisterRef.current = null;
      }
      resetSendState();
      setSendError(ENTITY_SHARE_TRANSPORT_ERROR);
    }
  };

  const handleRetryLoad = () => {
    setReloadKey((value) => value + 1);
  };

  const handleClose = () => {
    if (sendingRef.current) return;
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      scrollable
      className="project-modal entity-share-picker"
      aria-labelledby="entity-share-title"
    >
      <Modal.Header>
        <Modal.Title id="entity-share-title">Надіслати картку</Modal.Title>
        <div
          className="modal-btn-close"
          onClick={handleClose}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleClose()}
          aria-disabled={sending || undefined}
        >
          <i className="fa-solid fa-xmark fa-2x"></i>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Nav variant="tabs" className="mb-3 entity-share-picker__tabs">
          <Nav.Item className="entity-share-picker__tab-item">
            <Nav.Link
              className="entity-share-picker__tab-link"
              active={activeTab === "clients"}
              onClick={() => !sending && setActiveTab("clients")}
              disabled={sending}
            >
              Клієнти
            </Nav.Link>
          </Nav.Item>
          <Nav.Item className="entity-share-picker__tab-item">
            <Nav.Link
              className="entity-share-picker__tab-link"
              active={activeTab === "properties"}
              onClick={() => !sending && setActiveTab("properties")}
              disabled={sending}
            >
              Об&apos;єкти
            </Nav.Link>
          </Nav.Item>
        </Nav>
        <input
          type="search"
          className="form-control mb-3"
          placeholder="Пошук"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Пошук"
          disabled={sending}
        />
        {sendError && (
          <div
            className="entity-share-picker__state entity-share-picker__state--error py-2"
            role="alert"
          >
            {sendError}
          </div>
        )}
        {sending && (
          <div className="entity-share-picker__state text-center py-2" role="status">
            <Spinner animation="border" size="sm" />
            <span className="ms-2">Надсилання…</span>
          </div>
        )}
        {loading && (
          <div className="entity-share-picker__state text-center py-3">
            <Spinner animation="border" size="sm" role="status" />
            <span className="ms-2">Завантаження…</span>
          </div>
        )}
        {!loading && error && (
          <div className="entity-share-picker__state entity-share-picker__state--error py-2">
            <div>{error}</div>
            <Button
              variant="link"
              className="px-0"
              onClick={handleRetryLoad}
              disabled={sending}
            >
              Повторити
            </Button>
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <div className="entity-share-picker__state entity-share-picker__state--empty py-2">
            {EMPTY_BOTH_PARTICIPANTS}
          </div>
        )}
        {!loading && !error && items.length > 0 && (
          <>
            <ul className="entity-share-list">
              {items.map((item) => {
                const accessibleName = item.secondary
                  ? `${item.label}, ${item.secondary}`
                  : item.label;
                return (
                  <li key={`${item.type}-${item.id}`} className="entity-share-list__item">
                    <button
                      type="button"
                      className="entity-share-row"
                      disabled={sending}
                      onClick={() => handleSelect(item)}
                      aria-label={accessibleName}
                      title={item.label}
                    >
                      <span className="entity-share-row__media" aria-hidden="true">
                        {item.type === "property" && item.thumbnail ? (
                          <img src={item.thumbnail} alt="" className="entity-share-row__thumb" />
                        ) : (
                          <i
                            className={
                              item.type === "client"
                                ? "fa-regular fa-user"
                                : "fa-solid fa-house"
                            }
                          />
                        )}
                      </span>
                      <span className="entity-share-row__body">
                        <span className="entity-share-row__primary">{item.label}</span>
                        {item.secondary ? (
                          <span className="entity-share-row__secondary">{item.secondary}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {loadMoreError && (
              <div
                className="entity-share-picker__state entity-share-picker__state--error py-2"
                role="alert"
              >
                {loadMoreError}
              </div>
            )}
            {hasMore && (
              <div className="text-center py-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleLoadMore}
                  disabled={loadingMore || sending}
                >
                  {loadingMore ? "Завантаження…" : "Показати ще"}
                </Button>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={sending}>
          Закрити
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
